package com.essntl.features.invoice.ui.addeditinvoice.components.content

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.width
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.essntl.features.invoice.ui.addeditinvoice.AddEditInvoiceState
import com.essntl.features.paymentlink.domain.model.PaymentLinkModel
import com.essntl.uikit.components.button.primary.EsSmallPrimaryButton
import com.essntl.uikit.components.expandable.ExpandableCardHeader
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsModifiers
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlin.math.roundToInt

@Composable
fun AddEditInvoicePaymentLinks(
    state: AddEditInvoiceState,
    deactivatePaymentLink: (PaymentLinkModel, active: Boolean) -> Unit,
    addPaymentLink: (amount: Int, paymentParameterIds: Set<String>) -> Unit,
) {
    var isExpanded by remember { mutableStateOf(true) }
    var isCreatePaymentDialogExpanded by rememberSaveable { mutableStateOf(false) }

    val paymentLinks = state.getPaymentLinks.data.orEmpty()

    Column(
        modifier = EsModifiers.LargeSectionModifier,
    ) {
        ExpandableCardHeader(
            title = "Payment Links",
            rowContent = {
                Spacer(modifier = Modifier.width(1.5.spacing))

                EsSmallPrimaryButton(
                    onClick = {
                        isCreatePaymentDialogExpanded = true
                    },
                    text = "Add",
                    prefixIconRes = EsIcons.Add,
                    loading = state.addPaymentLink.isLoading,
                )
            },
            isExpanded = isExpanded,
            onClick = {
                isExpanded = !isExpanded
            },
        )

        AnimatedVisibility(
            visible = isExpanded,
        ) {
            when {
                state.getPaymentLinks.isLoading ->
                    EsSpinner()

                state.getPaymentLinks.isFailure ->
                    Text(
                        text = "Something goes wrong!",
                        color = MaterialTheme.esColorScheme.textSecondary,
                        style = MaterialTheme.esTypography.p1Medium16,
                    )

                paymentLinks.isEmpty() ->
                    Box(
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            text = "Nothing was found",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p1Medium16,
                        )
                    }

                else ->
                    Column {
                        paymentLinks.forEachIndexed { index, paymentLink ->
                            PaymentLinkCard(
                                paymentLinkModel = paymentLink,
                                isAddPaymentLinkLoading = state.addPaymentLink.isLoading,
                                isDeactivatePaymentLinkLoading = paymentLink.linkId in state.deactivatePaymentLinkLoadingSet,
                                deactivatePaymentLink = { active ->
                                    deactivatePaymentLink(paymentLink, active)
                                },
                            )

                            if (index != paymentLinks.lastIndex)
                                HorizontalDivider(
                                    color = MaterialTheme.esColorScheme.borderPrimary,
                                )
                        }
                    }
            }
        }
    }

    val balance = remember(state.getQuickbooksInvoice) {
        ((state.getQuickbooksInvoice.data?.Balance ?: 0.0) * 100).roundToInt()
    }

    val newPaymentLinkCount = if (paymentLinks.isEmpty()) " " else ".${paymentLinks.size + 1} "

    CreatePaymentLinkDialog(
        isOpened = isCreatePaymentDialogExpanded,
        onDismissRequest = {
            isCreatePaymentDialogExpanded = false
        },
        balance = balance,
        loading = state.addPaymentLink.isLoading,
        paymentParameterList = state.getPaymentParameters.data.orEmpty(),
        addPaymentLink = addPaymentLink,
        productName = state.getQuickbooksInvoice.data?.DocNumber.orEmpty() +
            newPaymentLinkCount + state.getProposal.data?.primaryClient?.lastName.orEmpty(),
    )
}